/**
 * Router construction, separated from `main.tsx` so tests can build a router without
 * mounting the application.
 */
import type { QueryClient } from '@tanstack/react-query';
import { createRouter } from '@tanstack/react-router';

import { routeTree } from './routeTree.gen';

/*
 * Inferred on purpose: the router's type carries the whole generated route tree, which is
 * what gives `<Link to=...>` its compile-time path checking.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    /* Guards call `ensureIdentity(context.queryClient)`, so the client travels with the route. */
    context: { queryClient },
    defaultPreload: 'intent',
    /*
     * Spec 4.7: route-level pending components with skeletons. The delay stops a skeleton
     * flashing for a load that resolves from cache in a few milliseconds.
     */
    defaultPendingMs: 200,
    defaultPendingMinMs: 400,
    scrollRestoration: true,
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
